import {
  CTCSS_TONES,
  DTCS_CODES,
  compareImages,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const READ_BLOCK_SIZE = 0x40
const CLASSIC_CHANNEL_SIZE = 0x10
const CLASSIC_IMAGE_MAIN_SIZE = 0x1808
const CLASSIC_IMAGE_AUX_SIZE = 0x1948
const CLASSIC_NAMES_OFFSET = 0x1008

type ClassicMagic = readonly number[]

export type UV5RProfile = {
  protocol: 'uv5r'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: number
  channelMaximum: number
  nameLength: 7
  powerLevels: readonly string[]
  idents: readonly ClassicMagic[]
  auxBlock: boolean
  amBand?: readonly [number, number]
}

function magic(hex: string): ClassicMagic {
  return Array.from(
    { length: hex.length / 2 },
    (_, index) => Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16),
  )
}

const UV5R_291 = magic('50bbff20071225')
const UV5R_ORIG = magic('50bbff0125984d')
const UV82 = magic('50bbff20130105')
const UV6_NEW = magic('50bbff20120823')
const UV6_ORIG = magic('50bbff1203984d')
const F11 = magic('50bbff13a111dd')
const F8HP = magic('50bbff20140413')
const UV5G = magic('50bbff20120625')

export const CLASSIC_UV5R_PROFILES: readonly UV5RProfile[] = [
  {
    protocol: 'uv5r',
    id: 'baofeng-uv-5r-classic',
    vendor: 'Baofeng',
    model: 'UV-5R',
    summary: 'The classic UV-5R clone protocol, including original and BFB291 firmware.',
    tags: ['USB', 'Read + write', '128 channels'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'baofeng-uv-82-classic',
    vendor: 'Baofeng',
    model: 'UV-82',
    summary: 'Popular UV-82 family using the 9600-baud Baofeng clone protocol.',
    tags: ['USB', 'Read + write', '128 channels'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV82],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'baofeng-bf-f8hp-classic',
    vendor: 'Baofeng',
    model: 'BF-F8HP',
    summary: 'High-power BF-F8HP with the UV-5R memory layout.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV5R_291, F8HP],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'baofeng-uv-82hp',
    vendor: 'Baofeng',
    model: 'UV-82HP',
    summary: 'High-power UV-82 variant with selectable transmit power.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV82],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'baofeng-uv-6',
    vendor: 'Baofeng',
    model: 'UV-6',
    summary: 'UV-6 and UV-7 family without the auxiliary memory block.',
    tags: ['USB', 'Read + write', '128 channels'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: 128,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV6_NEW, UV6_ORIG],
    auxBlock: false,
  },
  {
    protocol: 'uv5r',
    id: 'baofeng-f-11',
    vendor: 'Baofeng',
    model: 'F-11',
    summary: 'F-11 compatible UV-5R-family radio.',
    tags: ['USB', 'Read + write', '128 channels'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [F11],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'radioddity-uv-5g-classic',
    vendor: 'Radioddity',
    model: 'UV-5G',
    summary: 'GMRS UV-5G using the classic Baofeng clone protocol.',
    tags: ['USB', 'Read + write', 'GMRS'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5G],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'retevis-rt-5r-classic',
    vendor: 'Retevis',
    model: 'RT-5R',
    summary: 'Retevis UV-5R-compatible radio.',
    tags: ['USB', 'Read + write', '128 channels'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'retevis-rt5r-classic',
    vendor: 'Retevis',
    model: 'RT5R',
    summary: 'Retevis RT5R using the generic UV-5R clone layout.',
    tags: ['USB', 'Read + write', '128 channels'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'radioddity-uv-5r-ex',
    vendor: 'Radioddity',
    model: 'UV-5R EX',
    summary: 'Radioddity UV-5R EX using the generic UV-5R driver.',
    tags: ['USB', 'Read + write', 'UV-5R compatible'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'ansoko-a-5r',
    vendor: 'Ansoko',
    model: 'A-5R',
    summary: 'Ansoko A-5R compatible with the generic UV-5R protocol.',
    tags: ['USB', 'Read + write', 'UV-5R compatible'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'rugged-rh5r',
    vendor: 'Rugged',
    model: 'RH5R',
    summary: 'Rugged RH5R using the generic UV-5R protocol.',
    tags: ['USB', 'Read + write', 'UV-5R compatible'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'radioddity-uv-5rx3',
    vendor: 'Radioddity',
    model: 'UV-5RX3',
    summary: 'Tri-band UV-5RX3 using the classic UV-5R memory protocol.',
    tags: ['USB', 'Read + write', 'Tri-band'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'baofeng-gt-5r',
    vendor: 'Baofeng',
    model: 'GT-5R',
    summary: 'GT-5R using the generic UV-5R protocol with TX restrictions.',
    tags: ['USB', 'Read + write', 'UV-5R compatible'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'tidradio-td-uv5r-tripower',
    vendor: 'TIDRADIO',
    model: 'TD-UV5R TriPower',
    summary: 'TIDRADIO tri-power UV-5R variant.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV5R_291, F8HP],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'techside-ti-f8-plus',
    vendor: 'TechSide',
    model: 'TI-F8+',
    summary: 'TechSide TI-F8+ using the BF-F8HP protocol.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV5R_291, F8HP],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'tenway-uv-5r-pro',
    vendor: 'Tenway',
    model: 'UV-5R Pro',
    summary: 'Tenway UV-5R Pro using the BF-F8HP protocol.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV5R_291, F8HP],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'radioddity-uv-82x3',
    vendor: 'Radioddity',
    model: 'UV-82X3',
    summary: 'Radioddity tri-band UV-82 variant.',
    tags: ['USB', 'Read + write', 'Tri-band'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV82],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'tenway-uv-82-pro',
    vendor: 'Tenway',
    model: 'UV-82 Pro',
    summary: 'Tenway UV-82 Pro with tri-power support.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV82],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'radioddity-uv-5g-pro',
    vendor: 'Radioddity',
    model: 'UV-5G Pro',
    summary: 'UV-5G Pro with airband AM receive and the classic UV-5R map.',
    tags: ['USB', 'Read + write', 'Airband AM'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
    amBand: [108_000_000, 136_000_000],
  },
  {
    protocol: 'uv5r',
    id: 'baofeng-uv-5g-pro-classic',
    vendor: 'Baofeng',
    model: 'UV-5G Pro',
    summary: 'Baofeng UV-5G Pro with airband AM receive.',
    tags: ['USB', 'Read + write', 'Airband AM'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
    amBand: [108_000_000, 136_000_000],
  },
  {
    protocol: 'uv5r',
    id: 'intek-kt-980hp',
    vendor: 'Intek',
    model: 'KT-980HP',
    summary: 'Intek KT-980HP high-power UV-5R-compatible radio.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV5R_291],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'baofeng-uv-5x',
    vendor: 'Baofeng',
    model: 'UV-5X',
    summary: 'Baofeng UV-5X using the generic UV-5R clone layout.',
    tags: ['USB', 'Read + write', 'UV-5R compatible'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'retevis-rt5',
    vendor: 'Retevis',
    model: 'RT5',
    summary: 'Retevis RT5 using the generic UV-5R clone layout.',
    tags: ['USB', 'Read + write', 'UV-5R compatible'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'retevis-rt5rv',
    vendor: 'Retevis',
    model: 'RT5RV',
    summary: 'Retevis RT5RV using the generic UV-5R clone layout.',
    tags: ['USB', 'Read + write', 'UV-5R compatible'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Low'],
    idents: [UV5R_291, UV5R_ORIG],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'retevis-rt5-tri-power',
    vendor: 'Retevis',
    model: 'RT5(tri-power)',
    summary: 'Retevis RT5 tri-power using the BF-F8HP clone layout.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV5R_291, F8HP],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'radioddity-ga-5s',
    vendor: 'Radioddity',
    model: 'GA-5S',
    summary: 'Radioddity GA-5S using the BF-F8HP clone layout.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV5R_291, F8HP],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'baofeng-uv-5xp',
    vendor: 'Baofeng',
    model: 'UV-5XP',
    summary: 'Baofeng UV-5XP using the BF-F8HP clone layout.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV5R_291, F8HP],
    auxBlock: true,
  },
  {
    protocol: 'uv5r',
    id: 'techside-ts-t9-plus',
    vendor: 'TechSide',
    model: 'TS-T9+',
    summary: 'TechSide TS-T9+ using the BF-F8HP clone layout.',
    tags: ['USB', 'Read + write', 'Tri-power'],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: 127,
    nameLength: 7,
    powerLevels: ['High', 'Med', 'Low'],
    idents: [UV5R_291, F8HP],
    auxBlock: true,
  },
] as const

function encodeShortName(name: string, length: number): Uint8Array {
  const result = new Uint8Array(length).fill(0xff)
  const normalized = name
    .toUpperCase()
    .replace(/[^A-Z0-9 !@#$%^&*()+\-=[\]:;"'<>?,./]/g, '?')
    .slice(0, length)
  for (let index = 0; index < normalized.length; index += 1) {
    result[index] = normalized.charCodeAt(index)
  }
  return result
}

function decodeShortName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0xff ? 0x20 : value)),
  )
    .replace(/\0/g, ' ')
    .trim()
}

function decodeTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if (value >= 0x0258) {
    return {
      toneMode: 'ctcss',
      ctcss: value / 10,
      dtcs: null,
      dtcsPolarity: 'N',
    }
  }
  const reversed = value > 0x69
  const index = reversed ? value - 0x6a : value - 1
  return {
    toneMode: 'dtcs',
    ctcss: null,
    dtcs: DTCS_CODES[index] ?? null,
    dtcsPolarity: reversed ? 'R' : 'N',
  }
}

function encodeTone(
  target: Uint8Array,
  channel: WebChannel,
): void {
  let value = 0
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(channel.dtcs as (typeof DTCS_CODES)[number])
    if (index >= 0) value = index + 1 + (channel.dtcsPolarity === 'R' ? 0x69 : 0)
  }
  target[0] = value & 0xff
  target[1] = (value >> 8) & 0xff
}

function channelOffset(profile: UV5RProfile, number: number): number {
  return 8 + (number - profile.channelMinimum) * CLASSIC_CHANNEL_SIZE
}

export function parseClassicChannel(
  image: Uint8Array,
  profile: UV5RProfile,
  number: number,
): WebChannel {
  const offset = channelOffset(profile, number)
  const raw = image.slice(offset, offset + CLASSIC_CHANNEL_SIZE)
  if (raw.byteLength < CLASSIC_CHANNEL_SIZE || raw[0] === 0xff) {
    return {
      number,
      empty: true,
      name: '',
      rxFrequency: null,
      txFrequency: null,
      toneMode: 'none',
      ctcss: null,
      dtcs: null,
      dtcsPolarity: 'N',
      mode: 'FM',
      power: profile.powerLevels[0],
      skip: false,
    }
  }

  const nameOffset =
    CLASSIC_NAMES_OFFSET +
    (number - profile.channelMinimum) * CLASSIC_CHANNEL_SIZE
  const powerIndex = raw[14] & 0x03
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  return {
    number,
    empty: false,
    name: decodeShortName(image.slice(nameOffset, nameOffset + 7)),
    rxFrequency,
    txFrequency: decodeLbcd(raw.slice(4, 8)),
    ...decodeTone(raw.slice(8, 12)),
    mode:
      profile.amBand &&
      rxFrequency !== null &&
      rxFrequency >= profile.amBand[0] &&
      rxFrequency < profile.amBand[1]
        ? 'AM'
        : (raw[15] & 0x40) !== 0
          ? 'FM'
          : 'NFM',
    power: profile.powerLevels[powerIndex] ?? profile.powerLevels[0],
    skip: (raw[15] & 0x04) === 0,
  }
}

export function parseClassicChannels(
  image: Uint8Array,
  profile: UV5RProfile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum - profile.channelMinimum + 1 },
    (_, index) => parseClassicChannel(image, profile, index + profile.channelMinimum),
  )
}

export function applyClassicChannel(
  image: Uint8Array,
  profile: UV5RProfile,
  channel: WebChannel,
): void {
  const offset = channelOffset(profile, channel.number)
  const nameOffset =
    CLASSIC_NAMES_OFFSET +
    (channel.number - profile.channelMinimum) * CLASSIC_CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CLASSIC_CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    image.subarray(nameOffset, nameOffset + 7).fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')

  const wasEmpty = raw[0] === 0xff
  const preservedPower = wasEmpty ? 0 : raw[14] & 0xfc
  const preservedFlags = wasEmpty ? 0 : raw[15] & 0xbb
  if (wasEmpty) raw.fill(0xff)
  raw.fill(0, 0, 8)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  raw[12] = wasEmpty ? 0 : raw[12]
  raw[13] = wasEmpty ? 0 : raw[13]
  encodeTone(raw.subarray(8, 10), channel)
  raw.set(raw.slice(8, 10), 10)
  const powerIndex = Math.max(0, profile.powerLevels.indexOf(channel.power))
  raw[14] = preservedPower | (powerIndex & 0x03)
  raw[15] =
    preservedFlags |
    (channel.mode === 'FM' ? 0x40 : 0) |
    (channel.skip ? 0 : 0x04)
  image.set(encodeShortName(channel.name, 7), nameOffset)
}

function totalDownloadBlocks(profile: UV5RProfile): number {
  return profile.auxBlock ? 408 : 97
}

function normalizeIdent(response: Uint8Array): Uint8Array {
  if (response.byteLength === 8) return response
  if (response.byteLength === 12) {
    return Uint8Array.of(
      response[0],
      response[3],
      response[5],
      ...response.slice(7),
    )
  }
  throw new Error(`Unexpected ${response.byteLength}-byte radio identity.`)
}

async function identify(
  profile: UV5RProfile,
  transport: RadioTransport,
): Promise<Uint8Array> {
  let lastError: unknown = null
  for (const ident of profile.idents) {
    try {
      await transport.discardInput()
      for (const byte of ident) {
        await transport.write(Uint8Array.of(byte))
        await new Promise((resolve) => setTimeout(resolve, 10))
      }
      if ((await transport.readExact(1, 1000))[0] !== ACK) continue
      await transport.write(Uint8Array.of(0x02))
      const response: number[] = []
      for (let index = 0; index < 12; index += 1) {
        const byte = (await transport.readExact(1, 1000))[0]
        response.push(byte)
        if (byte === 0xdd) break
      }
      const normalized = normalizeIdent(Uint8Array.from(response))
      await transport.write(Uint8Array.of(ACK))
      if ((await transport.readExact(1, 1000))[0] !== ACK) {
        throw new Error('The radio refused clone mode.')
      }
      return normalized
    } catch (cause) {
      lastError = cause
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error(
        `The ${profile.vendor} ${profile.model} radio did not recognize any known programming identity. Confirm the exact model selected and try again.`,
      )
}

async function readBlock(
  transport: RadioTransport,
  address: number,
  size: number,
  firstCommand = false,
): Promise<Uint8Array> {
  await transport.write(
    Uint8Array.of(0x53, (address >> 8) & 0xff, address & 0xff, size),
  )
  if (!firstCommand && (await transport.readExact(1, 1200))[0] !== ACK) {
    throw new Error(`The radio refused read block 0x${address.toString(16)}.`)
  }
  const header = await transport.readExact(4, 1500)
  if (
    header[0] !== 0x58 ||
    header[1] !== ((address >> 8) & 0xff) ||
    header[2] !== (address & 0xff) ||
    header[3] !== size
  ) {
    throw new Error(`Unexpected read reply for block 0x${address.toString(16)}.`)
  }
  const payload = await transport.readExact(size, 2000)
  await transport.write(Uint8Array.of(ACK))
  await new Promise((resolve) => setTimeout(resolve, 50))
  return payload
}

async function firmwareInfo(
  transport: RadioTransport,
): Promise<{ version: Uint8Array; droppedByte: boolean }> {
  await readBlock(transport, 0x1e80, READ_BLOCK_SIZE, true)
  const block1 = await readBlock(transport, 0x1ec0, READ_BLOCK_SIZE)
  const block2 = await readBlock(transport, 0x1fc0, READ_BLOCK_SIZE)
  return {
    version: block1.slice(48, 62),
    droppedByte: block2[15] === 0xff,
  }
}

function isOriginal(version: Uint8Array): boolean {
  const text = new TextDecoder('ascii').decode(version)
  const index = text.indexOf('BFB')
  if (index < 0) return false
  const value = Number.parseInt(text.slice(index + 3, index + 6), 10)
  return Number.isFinite(value) && value < 291
}

export async function readClassicRadioWithTransport(
  profile: UV5RProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const ident = await identify(profile, transport)
  const total = totalDownloadBlocks(profile)
  let completed = 0
  let droppedByte = false
  let version = new Uint8Array(0)

  if (!profile.auxBlock) {
    await readBlock(transport, 0x0000, READ_BLOCK_SIZE, true)
    completed += 1
  } else {
    const info = await firmwareInfo(transport)
    version = new Uint8Array(info.version)
    droppedByte = info.droppedByte
    completed += 3
  }

  const image = new Uint8Array(
    profile.auxBlock ? CLASSIC_IMAGE_AUX_SIZE : CLASSIC_IMAGE_MAIN_SIZE,
  )
  image.set(ident, 0)
  let imageOffset = 8

  for (let address = 0; address < 0x1800; address += READ_BLOCK_SIZE) {
    const block = await readBlock(transport, address, READ_BLOCK_SIZE)
    image.set(block, imageOffset)
    imageOffset += block.byteLength
    completed += 1
    onProgress?.({ current: completed, total, label: 'Reading UV-5R memory' })
  }

  if (profile.auxBlock) {
    if (droppedByte) {
      for (let address = 0x1ec0; address < 0x1fc0; address += READ_BLOCK_SIZE) {
        const block = await readBlock(transport, address, READ_BLOCK_SIZE)
        image.set(block, imageOffset)
        imageOffset += block.byteLength
        completed += 1
      }
      for (let address = 0x1fc0; address < 0x2000; address += 0x10) {
        const block = await readBlock(transport, address, 0x10)
        image.set(block, imageOffset)
        imageOffset += block.byteLength
        completed += 1
      }
    } else {
      for (let address = 0x1ec0; address < 0x2000; address += READ_BLOCK_SIZE) {
        const block = await readBlock(transport, address, READ_BLOCK_SIZE)
        image.set(block, imageOffset)
        imageOffset += block.byteLength
        completed += 1
      }
    }
  }

  const versionText = new TextDecoder('ascii').decode(version).replace(/\0/g, '').trim()
  const identText = Array.from(ident, (value) =>
    value >= 0x20 && value < 0x7f ? String.fromCharCode(value) : '',
  )
    .join('')
    .trim()
  return {
    image,
    reportedModel: identText || versionText || 'UV-5R family',
  }
}

async function sendBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(4 + data.byteLength)
  frame.set([0x58, (address >> 8) & 0xff, address & 0xff, data.byteLength])
  frame.set(data, 4)
  await transport.write(frame)
  await new Promise((resolve) => setTimeout(resolve, 50))
  if ((await transport.readExact(1, 1500))[0] !== ACK) {
    throw new Error(`The radio rejected write block 0x${address.toString(16)}.`)
  }
}

async function auxiliaryData(
  transport: RadioTransport,
): Promise<{
  version: Uint8Array
  area1: Uint8Array
  area2: Uint8Array
  area3: Uint8Array
  droppedByte: boolean
}> {
  const block0 = await readBlock(transport, 0x1e80, READ_BLOCK_SIZE, true)
  const block1 = await readBlock(transport, 0x1ec0, READ_BLOCK_SIZE)
  const block2 = await readBlock(transport, 0x1f00, READ_BLOCK_SIZE)
  const block3 = await readBlock(transport, 0x1f40, READ_BLOCK_SIZE)
  const block4 = await readBlock(transport, 0x1f80, READ_BLOCK_SIZE)
  const block5 = await readBlock(transport, 0x1fc0, READ_BLOCK_SIZE)
  void block0
  return {
    version: block1.slice(48, 62),
    area1: Uint8Array.from([...block2, ...block3.slice(0, 32)]),
    area2: block3.slice(48, 64),
    area3: block4.slice(16, 64),
    droppedByte: block5[15] === 0xff,
  }
}

function rangesEqual(
  image: Uint8Array,
  start: number,
  end: number,
  radio: Uint8Array,
): boolean {
  if (image.byteLength !== radio.byteLength) return false
  for (let index = 0; index < image.byteLength; index += 1) {
    if (image[start + index] !== radio[index]) return false
  }
  return true
}

export async function writeClassicRadioWithTransport(
  profile: UV5RProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  const ident = await identify(profile, transport)
  if (
    image.byteLength < 8 ||
    ((ident[3] === 0x02 || image[3] === 0x02) && ident[3] !== image[3])
  ) {
    throw new Error('This image belongs to a different band variant of this radio.')
  }

  let droppedByte = false
  let originalImage = false
  let auxMatched = false
  if (profile.auxBlock && image.byteLength >= CLASSIC_IMAGE_AUX_SIZE) {
    const aux = await auxiliaryData(transport)
    droppedByte = aux.droppedByte
    originalImage = isOriginal(image.slice(0x1838, 0x1846))
    if (originalImage !== isOriginal(aux.version)) {
      throw new Error(
        'This image was created by incompatible UV-5R firmware. Read the radio again and edit that image.',
      )
    }
    auxMatched =
      rangesEqual(image, 0x1848, 0x18a8, aux.area1) &&
      rangesEqual(image, 0x18b8, 0x18c8, aux.area2) &&
      rangesEqual(image, 0x18d8, 0x1908, aux.area3)
  }
  if (!profile.auxBlock) {
    await readBlock(transport, 0x0000, READ_BLOCK_SIZE, true)
  }

  const mainRanges: Array<readonly [number, number]> = [
    [0x0008, 0x0cf8],
    [0x0d08, 0x0df8],
    [0x0e08, 0x1808],
  ]
  const auxRanges: Array<readonly [number, number]> = originalImage
    ? [
        [0x1ee0, 0x1ef0],
        [0x1fc0, 0x1fe0],
      ]
    : droppedByte || auxMatched
      ? [[0x1ec0, 0x2000]]
      : [
          [0x1ee0, 0x1ef0],
          [0x1f60, 0x1f70],
          [0x1f80, 0x1f90],
          [0x1fc0, 0x1fd0],
        ]

  const mainBlockCount = mainRanges.reduce(
    (count, [start, end]) => count + Math.ceil((end - start) / 0x10),
    0,
  )
  const total =
    mainBlockCount +
    (image.byteLength >= CLASSIC_IMAGE_AUX_SIZE
      ? auxRanges.reduce(
          (count, [start, end]) => count + Math.ceil((end - start) / 0x10),
          0,
        )
      : 0)
  let completed = 0
  for (const [start, end] of mainRanges) {
    for (let imageOffset = start; imageOffset < end; imageOffset += 0x10) {
      await sendBlock(
        transport,
        imageOffset - 8,
        image.slice(imageOffset, imageOffset + 0x10),
      )
      completed += 1
      onProgress?.({ current: completed, total, label: 'Writing UV-5R memory' })
    }
  }

  if (image.byteLength >= CLASSIC_IMAGE_AUX_SIZE) {
    for (const [start, end] of auxRanges) {
      for (let address = start; address < end; address += 0x10) {
        const imageOffset = 0x1808 + (address - 0x1ec0)
        await sendBlock(
          transport,
          address,
          image.slice(imageOffset, imageOffset + 0x10),
        )
        completed += 1
        onProgress?.({ current: completed, total, label: 'Writing UV-5R settings' })
      }
    }
  }
}

export { CTCSS_TONES, DTCS_CODES, compareImages }
